import { useEffect, useRef } from 'react';
import { classifyPosScanPayload, POS_SCAN_KIND } from '../utils/posScanCode';

function isEditableTarget(target) {
  if (!target || typeof target !== 'object') return false;
  const el = target;
  if (el.isContentEditable) return true;
  const tag = el.tagName?.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (el.closest?.('[data-pos-scan-ignore="true"]')) return true;
  return false;
}

/**
 * Écoute globale clavier (douchette USB/BT en mode clavier).
 * @param {{ enabled: boolean, onRetailGtin: (code: string) => void, onIgnoredKind?: (kind: string) => void, debounceMs?: number }} opts
 */
export function usePosBarcodeScanner({ enabled, onRetailGtin, onIgnoredKind, debounceMs = 350 }) {
  const bufferRef = useRef('');
  const lastScanRef = useRef({ gtin: '', at: 0 });

  useEffect(() => {
    if (!enabled) return undefined;

    const onKeyDown = (e) => {
      if (!enabled) return;
      if (typeof document !== 'undefined' && document.body?.dataset?.posBarcodeScan !== '1') {
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isEditableTarget(e.target)) return;

      if (e.key === 'Enter') {
        const raw = bufferRef.current.trim();
        bufferRef.current = '';
        if (!raw) return;
        e.preventDefault();
        const classified = classifyPosScanPayload(raw);
        if (
          classified.kind === POS_SCAN_KIND.RETAIL_GTIN ||
          classified.kind === POS_SCAN_KIND.CLICKORICO_INTERNAL
        ) {
          const now = Date.now();
          const code =
            classified.kind === POS_SCAN_KIND.RETAIL_GTIN ? classified.gtin : classified.internalCode;
          if (
            lastScanRef.current.gtin === code &&
            now - lastScanRef.current.at < debounceMs
          ) {
            return;
          }
          lastScanRef.current = { gtin: code, at: now };
          onRetailGtin?.(code);
          return;
        }
        onIgnoredKind?.(classified.kind);
        return;
      }

      if (e.key.length === 1) {
        bufferRef.current += e.key;
      }
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [enabled, onRetailGtin, onIgnoredKind, debounceMs]);
}
